import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { fetchStripeBalanceOverview, StripeBalanceOverview } from '../../lib/invoices/stripeBalance';

interface StripeBalancePanelProps {
  businessId: string;
  refreshKey?: number;
}

const fmtMoney = (cents: number, currency = 'USD') =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((cents || 0) / 100);

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const PAYOUT_STATUS_STYLES: Record<string, string> = {
  paid: 'bg-green-100 text-green-700',
  in_transit: 'bg-blue-100 text-blue-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
  canceled: 'bg-gray-200 text-gray-600',
};

export default function StripeBalancePanel({ businessId, refreshKey }: StripeBalancePanelProps) {
  const [data, setData] = useState<StripeBalanceOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [payoutsOpen, setPayoutsOpen] = useState(false);

  useEffect(() => {
    let active = true;
    if (!businessId) return;
    setLoading(true);
    fetchStripeBalanceOverview(businessId)
      .then((d) => { if (active) setData(d); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [businessId, refreshKey]);

  // Hide entirely only after a failed load (e.g. no Stripe data); while loading,
  // keep the containers visible with spinners so nothing pops in.
  if (!loading && !data) return null;

  const cur = data?.currency || 'USD';
  const Spinner = () => <div className="w-5 h-5 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" />;

  return (
    <div className="px-4 pt-4">
      <div className="grid grid-cols-1 gap-3">
        {/* Balance summary */}
        <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-4">
          <div className="flex items-center gap-2 mb-3">
            <img src="/Stripe (2).png" alt="Stripe" className="h-4 w-4 object-contain" />
            <h3 className="text-[14px] font-semibold text-gray-900">Balance</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] font-medium text-gray-500">Available</p>
              {loading || !data ? <div className="h-[28px] flex items-center"><Spinner /></div>
                : <p className="text-[22px] font-bold text-gray-900">{fmtMoney(data.availableCents, cur)}</p>}
            </div>
            <div>
              <p className="text-[11px] font-medium text-gray-500">Incoming</p>
              {loading || !data ? <div className="h-[28px] flex items-center"><Spinner /></div>
                : <p className="text-[22px] font-bold text-purple-600">{fmtMoney(data.pendingCents, cur)}</p>}
            </div>
          </div>
          {!loading && data && data.incoming.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-[11px] font-medium text-gray-500 mb-1.5">Becoming available</p>
              <div className="space-y-1.5">
                {data.incoming.slice(0, 4).map((inc) => (
                  <div key={inc.date} className="flex items-center justify-between text-[13px]">
                    <span className="text-gray-500">{formatDate(inc.date)}</span>
                    <span className="text-gray-900 font-medium">{fmtMoney(inc.amount_cents, cur)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent payouts (collapsed by default) */}
      {!loading && data && data.payouts.length > 0 && (
        <div className="mt-3 bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
          <button
            type="button"
            onClick={() => setPayoutsOpen((o) => !o)}
            className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <h3 className="text-[13px] font-semibold text-gray-700">Recent payouts</h3>
            <span className="flex items-center gap-1.5 text-[12px] text-gray-400">
              {data.payouts.length}
              {payoutsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
          </button>
          {payoutsOpen && (
          <div className="divide-y divide-gray-100 border-t border-gray-100">
            {data.payouts.map((p) => (
              <div key={p.id} className="flex items-center gap-3 px-4 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-gray-900">{fmtMoney(p.amount_cents, p.currency)}</div>
                  <div className="text-[11px] text-gray-500 truncate">
                    Payout to bank account{p.bank_name ? ` · ${p.bank_name}` : ''}{p.last4 ? ` ···· ${p.last4}` : ''}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium capitalize ${PAYOUT_STATUS_STYLES[p.status] || 'bg-gray-100 text-gray-600'}`}>
                    {p.status.replace(/_/g, ' ')}
                  </span>
                  {p.arrival_date && <div className="text-[11px] text-gray-400 mt-0.5">Arrives {formatDate(p.arrival_date)}</div>}
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      )}
    </div>
  );
}
